import {
  DAYS_PER_MONTH,
  DAYS_PER_WEEK,
  HOURS_PER_DAY,
  MINUTES_PER_DAY,
  MINUTES_PER_HOUR,
  MONTHS_PER_YEAR,
} from '../config/constants';
import type { GameTime } from '../models/GameTime';

/**
 * Pure, framework-free calendar math for the game clock (spec section 4).
 *
 * The clock uses a simplified calendar: every month has exactly 30 days and
 * a week has 7 days, so date math is trivial and fully deterministic. All
 * fields are derived from a single source of truth: `totalMinutes` (absolute
 * minutes since the epoch = year 1 / month 1 / day 1 / 00:00, a Monday).
 *
 * This module has NO Phaser dependency and is unit-testable on its own.
 */
export class GameClock {
  /** Build a GameTime purely from an absolute minute count. */
  static fromTotalMinutes(totalMinutes: number): GameTime {
    const tm = Math.max(0, Math.floor(totalMinutes));

    const minute = tm % MINUTES_PER_HOUR;
    const totalHours = Math.floor(tm / MINUTES_PER_HOUR);
    const hour = totalHours % HOURS_PER_DAY;
    const totalDays = Math.floor(tm / MINUTES_PER_DAY);

    const dayOfWeek = totalDays % DAYS_PER_WEEK; // 0 = Monday
    const day = (totalDays % DAYS_PER_MONTH) + 1; // 1-based
    const totalMonths = Math.floor(totalDays / DAYS_PER_MONTH);
    const month = (totalMonths % MONTHS_PER_YEAR) + 1; // 1-based
    const year = Math.floor(totalMonths / MONTHS_PER_YEAR) + 1; // 1-based

    return { year, month, day, dayOfWeek, hour, minute, totalMinutes: tm };
  }

  /** Convert calendar fields back to an absolute minute count. */
  static toTotalMinutes(
    time: Pick<GameTime, 'year' | 'month' | 'day' | 'hour' | 'minute'>
  ): number {
    const days =
      (time.year - 1) * MONTHS_PER_YEAR * DAYS_PER_MONTH +
      (time.month - 1) * DAYS_PER_MONTH +
      (time.day - 1);
    return days * MINUTES_PER_DAY + time.hour * MINUTES_PER_HOUR + time.minute;
  }

  /** Advance a GameTime by a number of minutes, returning a fresh GameTime. */
  static advance(time: GameTime, minutes: number): GameTime {
    return GameClock.fromTotalMinutes(time.totalMinutes + minutes);
  }

  /** True if `after` crossed midnight relative to `before` (new day started). */
  static crossedDay(before: GameTime, after: GameTime): boolean {
    return (
      Math.floor(after.totalMinutes / MINUTES_PER_DAY) >
      Math.floor(before.totalMinutes / MINUTES_PER_DAY)
    );
  }

  /** True if `after` started a new month relative to `before`. */
  static crossedMonth(before: GameTime, after: GameTime): boolean {
    return before.month !== after.month || before.year !== after.year;
  }

  /** Zero-padded "HH:MM". */
  static formatTime(time: GameTime): string {
    const hh = String(time.hour).padStart(2, '0');
    const mm = String(time.minute).padStart(2, '0');
    return `${hh}:${mm}`;
  }
}
